/**
 * Filas del Panel People (People-flow + pasos IT legacy) clasificadas en los
 * tres cajones que pidió KP (14/09): **en proceso**, **completados** y **con
 * errores**. Una sola fuente para las tres tabs, así los contadores de las
 * pestañas y las tablas siempre cuentan lo mismo.
 */

import { nextDaysRangeAR } from '@/../../shared/dates';
import { isInAlertWindow } from '@/../../shared/people-flow-window';
import { useMemo } from 'react';
import { trpc } from '@/lib/trpc';
import type { PeopleFlowAltaStatus } from '@/../../shared/types/people-flow';
import { legacyStepsFor, type PeopleFlowStep } from '@/components/users/onboarding-flow/flow-model';
import type { AltaPipeline } from '@/components/users/OnboardingPipeline';

export interface RowWithSteps {
  row: PeopleFlowAltaStatus;
  /** People-flow + IT legacy, para el pipeline y el progreso. */
  allSteps: PeopleFlowStep[];
  legacySteps: PeopleFlowStep[];
  hasError: boolean;
  /**
   * El onboarding de People terminó: ya ingresó y todos sus pasos del flow
   * quedaron cerrados (enviado / con algo a mano / no aplicó). Los pasos IT no
   * entran en la cuenta (son de otro equipo y pueden quedar "esperando" meses).
   */
  completed: boolean;
}

const TERMINAL = new Set(['ok', 'ok_pending', 'expired', 'na']);

/**
 * El panel muestra ingresos desde esta fecha (Sol 16/09): antes el agente no
 * existía bien y las altas históricas solo ensuciaban Process/Errors/Completed.
 * Las filas viejas siguen en `rows` (deep links ?alta= y detalle), pero no en
 * los cajones.
 */
export const PANEL_SINCE = '2026-09-07';

const dayOf = (row: PeopleFlowAltaStatus): string => (row.alta.fechaIngresoParsed ?? '').slice(0, 10);

/** Los próximos 7 días en Buenos Aires: hoy + 6 más, ambos inclusive ('YYYY-MM-DD', comparación por string como shared/dates). */
const next7DaysRangeAR = () => nextDaysRangeAR(7);

/**
 * Puro, para poder razonarlo: ingresó + flow cerrado + sin pausa ni error. El
 * paso "validation" NO entra en la cuenta: un dato que quedó vacío para siempre
 * en la planilla (foto, DNI dorso…) de alguien que ya ingresó no es un
 * onboarding "en proceso" — igual que `hasError` lo ignora pasada la semana
 * (hallazgo del code-reviewer: sin esto esas altas vivían en Proceso eternamente).
 */
export function isFlowCompleted(row: PeopleFlowAltaStatus, now = Date.now()): boolean {
  const ingresoMs = row.alta.fechaIngresoParsed ? Date.parse(row.alta.fechaIngresoParsed) : NaN;
  if (!Number.isFinite(ingresoMs) || ingresoMs > now) return false;
  const applicable = row.steps.filter((s) => s.status !== 'na' && String(s.stepId) !== 'validation');
  if (applicable.length === 0) return false;
  return applicable.every((s) => TERMINAL.has(s.status));
}

export function usePeopleFlowRows() {
  const flowQuery = trpc.peopleFlow.getFlowStatus.useQuery(undefined, { staleTime: 60_000 });
  const legacyQuery = trpc.users.getOnboardingStatus.useQuery(undefined, { staleTime: 60_000 });

  const rows: RowWithSteps[] = useMemo(() => {
    if (!flowQuery.data) return [];
    const legacyById = new Map<string, { pipeline: AltaPipeline | null; hasCorreo: boolean }>(
      (legacyQuery.data ?? []).map((l) => [
        String(l.id_alta),
        { pipeline: l.pipeline, hasCorreo: !!l.correo_winclap },
      ])
    );
    const now = Date.now();
    return flowQuery.data.map((row) => {
      const legacy = legacyById.get(row.alta.idAlta);
      const legacySteps = legacy ? legacyStepsFor(legacy.pipeline, legacy.hasCorreo) : [];
      // Datos faltantes solo alertan dentro de la ventana de KP (21/09): quien
      // entra en la próxima semana hábil o entró hace menos de 7 días. Alguien
      // que entra el 1/10 con un campo vacío el 21/09 no es un error todavía:
      // sus pasos ni vencieron. Las altas históricas quedan visibles sin rojo.
      const inWindow = isInAlertWindow(row.alta.fechaIngresoParsed, new Date(now));
      // Fuera de la ventana, el step "validation" con datos faltantes se muestra
      // "esperando" (el semáforo de la fila no se pone rojo por algo que no vence).
      const flowSteps = inWindow
        ? row.steps
        : row.steps.map((s) => (String(s.stepId) === 'validation' && s.status === 'error' ? { ...s, status: 'waiting' as const } : s));
      const allSteps = [...flowSteps, ...legacySteps];
      // El error del step "validation" es exactamente !validation.complete —
      // solo enciende rojo dentro de la ventana (si contara siempre, toda alta
      // histórica con un campo vacío inflaría "Con errores").
      const realErrors = allSteps.some(
        (s) => s.status === 'error' && String(s.stepId) !== 'validation'
      );
      const hasError = realErrors || (!row.validation.complete && inWindow);
      return {
        row,
        allSteps,
        legacySteps,
        hasError,
        completed: !hasError && isFlowCompleted(row, now),
      };
    });
  }, [flowQuery.data, legacyQuery.data]);

  // Solo ingresos desde PANEL_SINCE entran en los cajones del panel
  const visible = useMemo(() => rows.filter((r) => dayOf(r.row) >= PANEL_SINCE), [rows]);

  const buckets = useMemo(() => {
    const { from, to } = next7DaysRangeAR();
    return {
      /** Entran en los próximos 7 días (hoy inclusive). */
      upcoming: visible
        .filter((r) => { const d = dayOf(r.row); return d >= from && d <= to; })
        .sort((a, b) => dayOf(a.row).localeCompare(dayOf(b.row))),
      /** Todo lo que sigue vivo: futuros, en curso y con errores. */
      active: visible.filter((r) => !r.completed),
      completed: visible.filter((r) => r.completed),
      errors: visible.filter((r) => r.hasError),
    };
  }, [visible]);

  const statuses = useMemo(
    () => (flowQuery.data ?? []).filter((row) => dayOf(row) >= PANEL_SINCE),
    [flowQuery.data]
  );

  return {
    rows,
    buckets,
    statuses,
    isLoading: flowQuery.isLoading,
    error: flowQuery.error,
  };
}
